import React, { ReactNode, useEffect, useState } from 'react';
import IFund from '../../../interfaces/fund/IFund';
import IInvestExpertiseFiltersResult from '../../../interfaces/expertise/IInvestExpertiseFiltersResult';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Grid,
	Link,
	Stack,
	SxProps,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import * as XLSX from 'xlsx';
import AppCustomIcon from '../../../../../app/features/icon/AppIcon';
import AppFormatAmount from '../../../../../app/features/format/AppFormatAmount';
import investmentKpiLang from '../../../langs/investmentKpiLang';
import { useTranslation } from 'react-i18next';
import CAppInfoTooltip from '../../../../../app/components/CAppInfoTooltip';
import { InfoRounded, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import _ from 'lodash';
import { GridColDef, GridRowsProp, GridRenderCellParams } from '@mui/x-data-grid';
import CAppPaper from '../../../../../app/components/CAppPaper';
import styleVariables from '../../../../../app/styles/styleVariables';
import Carousel from 'react-material-ui-carousel';
import langInvestWordLang from '../../../langs/langInvestWordLang';
import CAppDataGrid from '../../../../../app/components/CAppDataGrid';
import { TInvestFundAssetRealizationStatusTag } from '../../../types/fund/TInvestFundAssetRealizationStatusTag';
import IInvestKpisByFundByInvestorByAsset from '../../../interfaces/IInvestKpisByFundByInvestorByAsset';
import investExpertiseAssetsTable from '../../../features/fund-type/langs/investExpertiseAssetsTable';
import removeAccents from 'remove-accents-esm';
import moment from 'moment';
import AppFormatX from '../../../../../app/features/format/AppFormatX';
import isoCountryCurrency from 'i18n-iso-countries';
import isoCountryCurrencyEN from 'i18n-iso-countries/langs/en.json';
import isoCountryCurrencyFR from 'i18n-iso-countries/langs/fr.json';
import investmentFundAssetsLang from '../../../langs/investmentFundAssetsLang';
import investmentTypeLang from '../../../features/fund-type/langs/investmentTypeLang';
import investExpertiseSynthesisVentureCapitalLang from '../../../features/fund-type/langs/investExpertiseSynthesisVentureCapitalLang';
import { TInvestFundCurrency } from '../../../types/fund/TInvestFundCurrency';

isoCountryCurrency.registerLocale(isoCountryCurrencyFR);
isoCountryCurrency.registerLocale(isoCountryCurrencyEN);

type VInvestExpertiseAssetsProps = {
	allFunds: IFund[];
	resultData: IInvestExpertiseFiltersResult;
	callExportData: boolean;
	resetCallExportData: () => void;
};
interface ValueWithCurrency {
	currency: TInvestFundCurrency | null | undefined;
	value: number | null | undefined;
}
interface IKpisByFundByInvestorByAssetDisplay {
	investorId: number;
	groupId: number;
	groupName: string;
	investorName: string;
	fundName: string;
	fundId: number;
	assetInvestmentDate?: Date;
	assetName: string;
	assetImgSrc?: string;
	assetCountryCode?: string;
	assetSectorName?: string;
	assetCurrency: string | null | undefined;
	assetStatus?: TInvestFundAssetRealizationStatusTag;
	kpiCommittedCapital: ValueWithCurrency;
	kpiCalledCapital: ValueWithCurrency;
	assetFMV: ValueWithCurrency;
	assetMultiple?: number;
}

const getCurrencyIfUniqueForKpis = (datas: IInvestKpisByFundByInvestorByAsset[]): TInvestFundCurrency | null => {
	if (!datas.length) return null;
	const firstCurrency = datas[0].kpis.currency;
	const isSameCurrency = datas.every((item) => item.kpis.currency === firstCurrency);
	return isSameCurrency ? firstCurrency : null;
};

const VInvestExpertiseAssets = (props: VInvestExpertiseAssetsProps) => {
	const { allFunds, resultData, callExportData, resetCallExportData } = props;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { t, i18n } = useTranslation();

	const [kpisByFundByInvestorByAsset, setKpisByFundByInvestorByAsset] = useState<
		IInvestKpisByFundByInvestorByAsset[] | null
	>(null);

	useEffect(() => {
		if (resultData) {
			setKpisByFundByInvestorByAsset(resultData.kpisByFundByInvestorByAsset ?? null);
		} else {
			setKpisByFundByInvestorByAsset(null);
		}
	}, [resultData]);

	const [funds, setFunds] = useState<IFund[] | null>(null);
	useEffect(() => {
		if (kpisByFundByInvestorByAsset && allFunds) {
			setFunds(allFunds.filter((f) => kpisByFundByInvestorByAsset.map((x) => x.fund?.id).includes(f.id)));
		} else {
			setFunds(null);
		}
	}, [allFunds, kpisByFundByInvestorByAsset]);

	// Clean date for display
	const [kpisByFundByInvestorByAssetDisplay, setKpisByFundByInvestorByAssetDisplay] = useState<
		IKpisByFundByInvestorByAssetDisplay[] | null
	>(null);
	useEffect(() => {
		if (kpisByFundByInvestorByAsset) {
			setKpisByFundByInvestorByAssetDisplay(
				kpisByFundByInvestorByAsset.map((x) => {
					return {
						investorId: x.investor.id,
						groupId: x.investor.group.id,
						groupName: x.investor.group.name,
						investorName: x.investor.name,
						fundName: x.fund.name,
						fundId: x.fund.id,
						assetInvestmentDate: x.asset.investment?.date,
						assetName: x.asset.name,
						assetImgSrc: x.asset.imgSrc,
						assetCountryCode: x.asset.countryCode,
						assetSectorName: x.asset.sectorNameText,
						assetCurrency: x.asset.currency,
						assetStatus: x.asset.investment?.reportingStatus,
						kpiCommittedCapital: { currency: x.asset.currency, value: x.kpis.committedCapital },
						kpiCalledCapital: { currency: x.asset.currency, value: x.kpis.calledCapital },
						assetFMV: { currency: x.asset.currency, value: x.asset.investment?.reportingFMV },
						assetMultiple: x.asset.investment?.multiple,
					};
				}),
			);
		}
	}, [kpisByFundByInvestorByAsset, t]);

	useEffect(() => {
		if (callExportData && kpisByFundByInvestorByAssetDisplay) {
			resetCallExportData();

			const groupNames = removeAccents(
				_.uniq(kpisByFundByInvestorByAssetDisplay.map((x) => x.groupName)).join(' - '),
			);

			const fileName = `${groupNames} - Assets.xlsx`;
			const kpisByFundByInvestorByAssetDisplayClean = _.cloneDeep(kpisByFundByInvestorByAssetDisplay).map(
				(x) => ({
					groupName: x.groupName ?? '',
					investorName: x.investorName ?? '',
					fundName: x.fundName ?? '',
					assetName: x.assetName ?? '',
					assetCountryName: x.assetCountryCode
						? isoCountryCurrency.getName(x.assetCountryCode, i18n.language)
						: '',

					assetSectorName: x.assetSectorName ?? '',
					assetCurrency: x.assetCurrency ?? '',
					assetStatus: x.assetStatus
						? x.assetStatus === 'realized'
							? t(investmentFundAssetsLang.reporting_status_realized.tag)
							: x.assetStatus === 'unrealized'
							? t(investmentFundAssetsLang.reporting_status_unrealized.tag)
							: x.assetStatus === 'partial'
							? t(investmentFundAssetsLang.reporting_status_partial.tag)
							: ''
						: '',
					assetInvestmentDate: x.assetInvestmentDate
						? Number.parseInt(moment(x.assetInvestmentDate).format('YYYY'))
						: '',
					kpiCommittedCapital: x.kpiCommittedCapital ?? t(langInvestWordLang.nc.tag),
					assetFMV: x.assetFMV ?? t(langInvestWordLang.nc.tag),
					assetMultiple: x.assetMultiple ?? t(langInvestWordLang.nc.tag),
				}),
			);

			const headers = [
				t(investExpertiseAssetsTable.group.tag),
				t(investExpertiseAssetsTable.investor.tag),
				t(investExpertiseAssetsTable.fund.tag),
				t(investExpertiseAssetsTable.asset.tag),
				t(investExpertiseAssetsTable.country.tag),
				t(investExpertiseAssetsTable.sector.tag),
				t(investExpertiseAssetsTable.asset_currency.tag),
				t(investExpertiseAssetsTable.asset_status.tag),
				t(investExpertiseAssetsTable.investment_year.tag),
				t(investExpertiseAssetsTable.invested_capital.tag),
				t(investExpertiseAssetsTable.distributions.tag),
				t(investExpertiseAssetsTable.fmv.tag),
				t(investExpertiseAssetsTable.multiple.tag),
			];

			const worksheet = XLSX.utils.json_to_sheet(kpisByFundByInvestorByAssetDisplayClean);
			const workbook = XLSX.utils.book_new();
			XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Portfolio');

			worksheet['!cols'] = _.keys(kpisByFundByInvestorByAssetDisplayClean[0]).map((field, index) => {
				const header = headers[index] ?? '';
				return {
					wch: Math.max(
						header.length,
						Math.max(
							...kpisByFundByInvestorByAssetDisplayClean.map(
								(obj: Record<string, unknown>) => String(obj[field] ?? '').length,
							),
						),
					),
				};
			});
			XLSX.writeFileXLSX(workbook, fileName);
		}
	}, [kpisByFundByInvestorByAssetDisplay, callExportData, resetCallExportData, t, i18n.language]);

	const [accordionMainGroups, setAccordionMainGroups] = useState<
		| {
				id: number;
				isOpen: boolean;
		  }[]
		| null
	>(null);

	const handleToggleMainGroup = (mainGroup: { id: number }) => {
		setAccordionMainGroups(
			accordionMainGroups?.map((x) => ({
				...x,
				isOpen: x.id === mainGroup.id ? !x.isOpen : x.isOpen,
			})) ?? null,
		);
	};

	// Get funds (funds here)
	useEffect(() => {
		if (funds) {
			setAccordionMainGroups(funds.map((fund) => ({ id: fund.id, isOpen: true })));
		} else {
			setAccordionMainGroups(null);
		}
	}, [funds]);

	const sxHeaderMainGroup = {
		'.group_name': {
			fontSize: '1.125rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#FFF',
		},
		'.kpi_label': {
			fontSize: '0.875rem',
			fontWeight: '400',
			lineHeight: '1.5rem',
			color: '#FFF',
		},
		'.kpi_value': {
			fontSize: '1rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#2ABDBB',
		},
	} as SxProps;

	const sxHeaderSubGroup = {
		'.group_name': {
			fontSize: '1.125rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#1A2B48',
		},
		'.kpi_label': {
			fontSize: '0.875rem',
			fontWeight: '400',
			lineHeight: '1.5rem',
			color: '#1A2B48',
		},
		'.kpi_value': {
			fontSize: '0.9rem',
			fontWeight: '600',
			lineHeight: '1.5rem',
			color: '#2ABDBB',
		},
	} as SxProps;

	const sxAccordionSummaryMainGroup =
		theme.palette.mode === 'dark'
			? {
					backgroundColor: '#1A2B48',
					border: '2px solid white',
			  }
			: {
					backgroundColor: '#1A2B48',
					border: '2px solid #1A2B48',
			  };

	const sxAccordionSummarySubGroup =
		theme.palette.mode === 'dark'
			? {
					color: 'white',
					border: '1px solid white',
					backgroundColor: '#1A2B48',
					'& svg': {
						color: 'white',
					},
					'& .group_name': {
						color: '#FFF!important',
					},

					'& .kpi_label': {
						color: '#FFF',
					},
			  }
			: {
					backgroundColor: '#FFF',
					border: '1px solid #FFF',
					'& svg': {
						color: '#1A2B48',
					},
			  };

	const [isLoading, setIsLoading] = useState(true);
	useEffect(() => {
		if (kpisByFundByInvestorByAsset && allFunds && accordionMainGroups) {
			setIsLoading(false);
		}
	}, [accordionMainGroups, kpisByFundByInvestorByAsset, allFunds]);

	return (
		<>
			{!isLoading ? (
				<Box id="expertise-content-assets">
					<Box
						sx={{
							marginBottom: '15px',
							padding: '6px 8px',
							alignItems: 'center',
							flexDirection: 'row',
							display: 'flex',
							backgroundColor:
								theme.palette.mode === 'light'
									? styleVariables.greyLightColor
									: styleVariables.primaryColor,
							fontSize: '0.85rem',
							color: theme.palette.primary.main,
							borderRadius: '7px',
						}}
					>
						<InfoRounded sx={{ height: '1rem' }} />{' '}
						{t(investmentTypeLang.assets_data_presented_are_transparent.tag)}
					</Box>
					{funds?.map((fund, index) => {
						const currentKpisByFundByInvestorByAsset = kpisByFundByInvestorByAsset?.filter(
							(x) => x.fund?.id === fund.id,
						);
						const assetsCurrency = getCurrencyIfUniqueForKpis(currentKpisByFundByInvestorByAsset ?? []);

						return (
							<Box
								key={index}
								sx={{
									marginBottom: '15px',
								}}
							>
								<Accordion
									expanded={
										isMobile
											? true
											: accordionMainGroups?.some((g) => g.id === fund.id && g.isOpen) ?? false
									}
									sx={{
										...sxHeaderMainGroup,
										background: 'none',
										boxShadow: 'none',
										borderRadius: '10px',
									}}
								>
									<AccordionSummary
										expandIcon={
											isMobile ? null : (
												<AppCustomIcon
													tag="chevron_up"
													invColor
													sx={{
														marginLeft: '12px',
														marginRight: '12px',
													}}
												/>
											)
										}
										sx={{
											color: 'white',
											borderRadius: '10px',
											...sxAccordionSummaryMainGroup,
										}}
										onClick={() => handleToggleMainGroup(fund)}
									>
										<Grid
											container
											spacing={1}
											alignItems="stretch"
											sx={{ marginRight: { sm: '36px', sx: '0px' } }}
										>
											<Grid item sm={3}>
												<Typography className="group_name">
													{fund.isCoinvestment
														? `${t(
																investExpertiseSynthesisVentureCapitalLang
																	.title_coinvestment.tag,
														  )} : `
														: `${t(
																investExpertiseSynthesisVentureCapitalLang.title_fund
																	.tag,
														  )} : `}{' '}
													{fund.name}
												</Typography>
											</Grid>
											<Grid item container spacing={1} xs={12} sm={9} alignItems="stretch">
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Typography className="kpi_label">
															{t(investmentKpiLang.fund_commitments.tag)}{' '}
														</Typography>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: { sm: 'translate(-10%)' },
																width: { sm: 'calc(100% + 10%)' },
															}}
														>
															<AppFormatAmount
																value={_.sumBy(
																	currentKpisByFundByInvestorByAsset,
																	'kpis.committedCapital',
																)}
																addSpace
																currency={assetsCurrency}
															/>
														</Typography>
													</Grid>
												</Grid>
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Typography className="kpi_label">
															{t(investmentKpiLang.contributed_capital.tag)}{' '}
														</Typography>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: { sm: 'translate(-10%)' },
																width: { sm: 'calc(100% + 10%)' },
															}}
														>
															<AppFormatAmount
																value={_.sumBy(
																	currentKpisByFundByInvestorByAsset,
																	'kpis.calledCapital',
																)}
																addSpace
																currency={assetsCurrency}
															/>
														</Typography>
													</Grid>
												</Grid>
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Box style={{ height: '100%' }}>
															<Typography
																className="kpi_label"
																sx={{
																	transform: { sm: 'translate(-10%)' },
																}}
															>
																{t(investmentKpiLang.total_value.tag)}{' '}
															</Typography>
														</Box>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Box style={{ height: '100%' }}>
															<Typography
																className="kpi_value"
																sx={{
																	transform: { sm: 'translate(-10%)' },
																	width: { sm: 'calc(100% + 10%)' },
																}}
															>
																<AppFormatAmount
																	value={_.sumBy(
																		currentKpisByFundByInvestorByAsset,
																		'kpis.totalValue',
																	)}
																	addSpace
																	currency={assetsCurrency}
																/>
															</Typography>
														</Box>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</AccordionSummary>
									<AccordionDetails
										sx={{
											padding: { sm: '15px 0px 15px 20px', xs: '15px 0px' },
										}}
									>
										<ViewFundByAssetRealizationStatus
											fund={fund}
											isMobile={isMobile}
											sxAccordionSummarySubGroup={sxAccordionSummarySubGroup}
											sxHeaderSubGroup={sxHeaderSubGroup}
											kpisByFundByInvestorByAsset={kpisByFundByInvestorByAsset ?? []}
											kpisByFundByInvestorByAssetDisplay={
												kpisByFundByInvestorByAssetDisplay ?? []
											}
										/>
									</AccordionDetails>
								</Accordion>
							</Box>
						);
					})}
				</Box>
			) : (
				'Loading...'
			)}
		</>
	);
};

type ViewFundByAssetRealizationStatusProps = {
	sxAccordionSummarySubGroup: SxProps;
	sxHeaderSubGroup: SxProps;
	isMobile: boolean;
	fund: IFund;
	kpisByFundByInvestorByAsset: IInvestKpisByFundByInvestorByAsset[];
	kpisByFundByInvestorByAssetDisplay: IKpisByFundByInvestorByAssetDisplay[];
};

type TaccordionSubGroup = {
	tag: 'unrealized' | 'realized';
	assetRealizationStatus: TInvestFundAssetRealizationStatusTag[];
	isOpen: boolean;
	isOpenDetails: boolean;
};

const ViewFundByAssetRealizationStatus = (props: ViewFundByAssetRealizationStatusProps) => {
	const {
		fund,
		isMobile,
		sxAccordionSummarySubGroup,
		sxHeaderSubGroup,
		kpisByFundByInvestorByAsset,
		kpisByFundByInvestorByAssetDisplay,
	} = props;
	const { t } = useTranslation();
	const theme = useTheme();

	const [accordionSubGroups, setAccordionSubGroups] = useState<TaccordionSubGroup[]>([
		{
			tag: 'unrealized',
			assetRealizationStatus: ['unrealized', 'partial'],
			isOpen: true,
			isOpenDetails: false,
		},
		{
			tag: 'realized',
			assetRealizationStatus: ['realized'],
			isOpen: true,
			isOpenDetails: false,
		},
	]);

	const handleToggleSubGroup = (subGroup: TaccordionSubGroup) => {
		setAccordionSubGroups(
			accordionSubGroups.map((x) => ({
				...x,
				isOpen: x.tag === subGroup.tag ? !x.isOpen : x.isOpen,
			})),
		);
	};

	const handleToggleSubGroupDetails = (subGroup: TaccordionSubGroup) => {
		setAccordionSubGroups(
			accordionSubGroups.map((x) => ({
				...x,
				isOpenDetails: x.tag === subGroup.tag ? !x.isOpenDetails : x.isOpenDetails,
			})),
		);
	};

	return (
		<>
			{accordionSubGroups &&
				accordionSubGroups.map((accordionSubGroup, index) => {
					if (
						kpisByFundByInvestorByAssetDisplay.some(
							(x) =>
								x.fundId === fund.id &&
								(x.assetStatus
									? accordionSubGroup.assetRealizationStatus.includes(x.assetStatus)
									: false),
						)
					) {
						const currentKpisByFundByInvestorByAsset = kpisByFundByInvestorByAsset.filter(
							(x) =>
								x.fund?.id === fund.id &&
								(x.asset.investment?.reportingStatus
									? accordionSubGroup.assetRealizationStatus.includes(
											x.asset.investment.reportingStatus,
									  )
									: false),
						);
						const assetsCurrency = getCurrencyIfUniqueForKpis(currentKpisByFundByInvestorByAsset);
						return (
							<Box
								key={index}
								sx={{
									marginBottom: '15px',
									opacity: accordionSubGroup.assetRealizationStatus.includes('realized')
										? '0.6'
										: '1',
								}}
							>
								<Accordion
									expanded={isMobile ? true : accordionSubGroup.isOpen}
									sx={{
										...sxHeaderSubGroup,
										background: 'transparent',
										boxShadow: 'none',
										borderRadius: '10px',
									}}
								>
									<AccordionSummary
										expandIcon={
											isMobile ? null : (
												<AppCustomIcon
													tag="chevron_up"
													sx={{
														marginLeft: '12px',
														marginRight: '12px',
													}}
												/>
											)
										}
										sx={{
											borderRadius: '10px',
											...sxAccordionSummarySubGroup,
											'&.Mui-expanded': {
												borderRadius: '10px 10px 0px 0px',
											},
										}}
										onClick={() => handleToggleSubGroup(accordionSubGroup)}
									>
										<Grid
											container
											spacing={1}
											alignItems="stretch"
											sx={{ marginRight: { sm: '36px', xs: '0px' } }}
										>
											<Grid item sm={3}>
												<Typography className="group_name">
													{accordionSubGroup.assetRealizationStatus.includes('realized')
														? t(
																investmentTypeLang
																	.asset_title_realization_status_realized.tag,
														  )
														: t(
																investmentTypeLang
																	.asset_title_realization_status_not_realized.tag,
														  )}
												</Typography>
												<Box sx={{ fontSize: '0.80rem' }}>
													{accordionSubGroup.assetRealizationStatus.includes('realized')
														? ''
														: t(
																investmentTypeLang
																	.asset_sub_title_realization_status_not_realized
																	.tag,
														  )}
												</Box>
											</Grid>
											<Grid item container spacing={1} xs={12} sm={9} alignItems="stretch">
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Typography
															className="kpi_label"
															sx={{
																transform: {
																	sm: 'translate(-5.5%)',
																},
															}}
														>
															{t(investmentKpiLang.fund_commitments.tag)}{' '}
														</Typography>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: {
																	sm: 'translate(-12%)',
																},
															}}
														>
															<AppFormatAmount
																value={_.sumBy(
																	currentKpisByFundByInvestorByAsset,
																	'kpis.committedCapital',
																)}
																addSpace
																currency={assetsCurrency}
															/>
														</Typography>
													</Grid>
												</Grid>
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Typography
															className="kpi_label"
															sx={{
																transform: {
																	sm: 'translate(-3.5%)',
																},
															}}
														>
															{t(investmentKpiLang.contributed_capital.tag)}{' '}
														</Typography>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Typography
															className="kpi_value"
															sx={{
																transform: { sm: 'translate(-7%)' },
															}}
														>
															<AppFormatAmount
																value={_.sumBy(
																	currentKpisByFundByInvestorByAsset,
																	'kpis.calledCapital',
																)}
																addSpace
																currency={assetsCurrency}
															/>
														</Typography>
													</Grid>
												</Grid>
												<Grid
													item
													container
													spacing={3}
													alignItems="stretch"
													sm={4}
													sx={{ textAlign: { sm: 'right', xs: 'left' } }}
												>
													<Grid item xs={8}>
														<Box style={{ height: '100%' }}>
															<Typography
																className="kpi_label"
																sx={{
																	transform: {
																		sm: 'translate(-11%)',
																	},
																}}
															>
																{t(investmentKpiLang.total_value.tag)}{' '}
															</Typography>
														</Box>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<Box style={{ height: '100%' }}>
															<Typography
																className="kpi_value"
																sx={{
																	transform: {
																		sm: 'translate(-10%)',
																	},
																}}
															>
																<AppFormatAmount
																	value={_.sumBy(
																		currentKpisByFundByInvestorByAsset,
																		'kpis.totalValue',
																	)}
																	addSpace
																	currency={assetsCurrency}
																/>
															</Typography>
														</Box>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</AccordionSummary>
									<AccordionDetails
										sx={{
											border: '1px solid white',
											marginBottom: '15px',
											paddingBottom: '0px',
											borderBottomLeftRadius: '10px',
											borderBottomRightRadius: '10px',
											backgroundColor: theme.palette.mode === 'dark' ? '#1A2B48' : '#FFF',
										}}
									>
										{!isMobile ? (
											<TableAssets
												data={kpisByFundByInvestorByAssetDisplay.filter(
													(x) =>
														x.fundId === fund.id &&
														(x.assetStatus
															? accordionSubGroup.assetRealizationStatus.includes(
																	x.assetStatus,
															  )
															: false),
												)}
											/>
										) : (
											<>
												<Link
													color="primary"
													sx={{
														position: 'relative',
														fontWeight: 600,
														fontSize: '0.875rem',
														lineHeight: '1.7',
													}}
													onClick={() => handleToggleSubGroupDetails(accordionSubGroup)}
												>
													{t(investmentTypeLang.get_x_assets_details.tag, {
														count: kpisByFundByInvestorByAssetDisplay.filter(
															(x) =>
																x.fundId === fund.id &&
																(x.assetStatus
																	? accordionSubGroup.assetRealizationStatus.includes(
																			x.assetStatus,
																	  )
																	: false),
														).length,
													})}

													{accordionSubGroups.some(
														(x) => x.tag === accordionSubGroup.tag && x.isOpenDetails,
														// accordionSubGroup.assetRealizationStatus.includes(
														// 	x.assetStatus,
														// ),
													) ? (
														<KeyboardArrowUpOutlined
															sx={{
																marginTop: '0px',
																position: 'absolute',
																top: '50%',
																transform: 'translateY(-50%)',
															}}
														/>
													) : (
														<KeyboardArrowDownOutlined
															sx={{
																marginTop: '0px',
																position: 'absolute',
																top: '50%',
																transform: 'translateY(-50%)',
															}}
														/>
													)}
												</Link>
											</>
										)}
									</AccordionDetails>
								</Accordion>
								{isMobile &&
								accordionSubGroups.some((x) => x.tag === accordionSubGroup.tag && x.isOpenDetails) ? (
									<BoxAssets
										fund={fund}
										data={kpisByFundByInvestorByAssetDisplay.filter(
											(x) =>
												x.fundId === fund.id &&
												(x.assetStatus
													? accordionSubGroup.assetRealizationStatus.includes(x.assetStatus)
													: false),
										)}
									/>
								) : (
									''
								)}
							</Box>
						);
					}
				})}
		</>
	);
};

type TableAssetsProps = {
	data: IKpisByFundByInvestorByAssetDisplay[];
};

const TableAssets = (props: TableAssetsProps) => {
	const { data } = props;

	const { t } = useTranslation();

	const [rows, setRows] = useState<GridRowsProp | null>(null);

	useEffect(() => {
		if (data) {
			setRows(
				_.orderBy(
					data.map((x, index) => {
						return {
							...x,
							id: index,
						};
					}),
					['investment.date'],
					['desc'],
				),
			);
		}
	}, [data]);

	const columns: GridColDef[] = [
		{
			field: 'assetName',
			headerName: t(investExpertiseAssetsTable.entreprise.tag),
			minWidth: 250,
			flex: 1,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="center"
							spacing={0}
							sx={{
								padding: '2px',
								width: '80px',
								height: '40px',
								backgroundColor: 'white',
								overflow: 'hidden',
								borderRadius: '10px',
								border: '1px solid ' + styleVariables.greyLightColor,
							}}
						>
							<Box
								component="img"
								sx={{
									height: '36px',
									maxWidth: '80px',

									// marginLeft: theme.palette.mode !== 'dark' ? '-10px' : '',
								}}
								src={params.row.assetImgSrc}
							/>
						</Stack>
						<Box>{params.value}</Box>
					</Stack>
				);
			},
		},
		{
			field: 'assetCurrency',
			headerName: t(investExpertiseAssetsTable.asset_currency.tag),
			width: 120,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => params.value.toUpperCase(),
		},
		{
			field: 'assetInvestmentDate',
			headerName: t(investExpertiseAssetsTable.investment_year.tag),
			width: 180,
			align: 'left',
			headerAlign: 'left',
			renderCell: (params: GridRenderCellParams) => (params.value ? moment(params.value).format('YYYY') : null),
		},
		{
			field: 'kpiCalledCapital',
			headerName: t(investExpertiseAssetsTable.invested_capital.tag),
			width: 150,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatAmount
						value={params.value.value}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={params.value.currency}
					/>
				);
			},
		},
		{
			field: 'assetFMV',
			headerName: t(investExpertiseAssetsTable.fmv.tag),
			width: 120,
			align: 'right',
			headerAlign: 'right',
			renderHeader: () => (
				<Box className="MuiDataGrid-columnHeaderTitle">
					{t(investExpertiseAssetsTable.fmv.tag)}
					<CAppInfoTooltip title={t(investExpertiseAssetsTable.fmv_tooltip.tag)} />
				</Box>
			),
			renderCell: (params: GridRenderCellParams) => {
				return (
					<AppFormatAmount
						value={params.value.value}
						addSpace
						textIfNull={t(langInvestWordLang.nc.tag)}
						currency={params.value.currency}
					/>
				);
			},
		},
		{
			field: 'assetMultiple',
			headerName: t(investExpertiseAssetsTable.multiple.tag),
			width: 130,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => {
				return <AppFormatX value={params.value} addSpace textIfNull={t(langInvestWordLang.nc.tag)} />;
			},
		},
	];

	const theme = useTheme();

	return (
		<>
			{rows ? (
				<Box>
					<CAppDataGrid
						sx={{
							marginLeft: '-10px',
							'*::-webkit-scrollbar': {
								height: '5px',
							},
							'*::-webkit-scrollbar-track': {
								backgroundColor: theme.palette.background.paper,
							},

							'*::-webkit-scrollbar-thumb': {
								backgroundColor: theme.palette.primary.main,
							},
						}}
						rows={rows}
						disableSelectionOnClick
						disableColumnSelector
						disableColumnMenu
						columns={columns}
						autoHeight
						hideFooter
					/>
				</Box>
			) : (
				''
			)}
		</>
	);
};

type BoxAssetsProps = {
	data: IKpisByFundByInvestorByAssetDisplay[];
	fund: IFund;
};

const BoxAssets = (props: BoxAssetsProps) => {
	const { data } = props;

	const { t } = useTranslation();
	const theme = useTheme();

	const [rows, setRows] = useState<GridRowsProp | null>(null);

	useEffect(() => {
		if (data) {
			setRows(
				data.map((x, index) => {
					return {
						...x,
						id: index,
					};
				}),
			);
		}
	}, [data]);

	return (
		<>
			{rows ? (
				<Box>
					<Carousel swipe indicators animation="fade" autoPlay={false}>
						{rows.map((row) => {
							return (
								<CAppPaper
									key={row.id}
									variant="outlined"
									sx={{
										backgroundColor:
											theme.palette.mode === 'dark' ? styleVariables.primaryColor : '#F4F9FF',
										marginBottom: '30px',
										border: '1px solid white',
									}}
								>
									<Grid container spacing={3}>
										<Grid item xs={12}>
											<BoxAssetItem
												label={t(investExpertiseAssetsTable.entreprise.tag)}
												value={
													<Stack direction="row" alignItems="center" spacing={2}>
														<Box
															component="img"
															sx={{
																maxheight: '52px',
																width: '80px',
																backgroundColor: '#F4F9FF',
																padding: '10px',
																borderRadius: '10px',
																marginLeft:
																	theme.palette.mode !== 'dark' ? '-10px' : '',
															}}
															src={row.assetImgSrc}
														/>
														<Box>{row.assetName}</Box>
													</Stack>
												}
											/>
										</Grid>
										<Grid item xs={5}>
											<BoxAssetItem
												label={t(investExpertiseAssetsTable.asset_currency.tag)}
												value={row.assetCurrency.toString().toUpperCase()}
											/>
										</Grid>
										<Grid item xs={7}>
											<BoxAssetItem
												label={t(investExpertiseAssetsTable.investment_year.tag)}
												value={
													row.assetInvestmentDate
														? moment(row.assetInvestmentDate).format('YYYY')
														: null
												}
											/>
										</Grid>
										<Grid item xs={5}>
											<BoxAssetItem
												label={t(investExpertiseAssetsTable.invested_capital.tag)}
												value={
													<AppFormatAmount
														value={row.kpiCalledCapital}
														addSpace
														textIfNull={t(langInvestWordLang.nc.tag)}
														currency={row.assetCurrency}
													/>
												}
											/>
										</Grid>
										<Grid item xs={7}>
											<BoxAssetItem
												label={t(investExpertiseAssetsTable.fmv.tag)}
												value={
													<AppFormatAmount
														value={row.assetFMV}
														addSpace
														textIfNull={t(langInvestWordLang.nc.tag)}
														currency={row.assetCurrency}
													/>
												}
											/>
										</Grid>
										<Grid item xs={7}>
											<BoxAssetItem
												label={t(investExpertiseAssetsTable.multiple.tag)}
												value={
													<AppFormatX
														value={row.assetMultiple}
														addSpace
														textIfNull={t(langInvestWordLang.nc.tag)}
													/>
												}
											/>
										</Grid>
									</Grid>
								</CAppPaper>
							);
						})}
					</Carousel>
				</Box>
			) : (
				''
			)}
		</>
	);
};

type BoxAssetItemProps = {
	label: string | ReactNode;
	value: string | number | ReactNode;
};
const BoxAssetItem = (props: BoxAssetItemProps) => {
	const theme = useTheme();
	const sxItem = {
		'.label': {
			fontWeight: 600,
			fontSize: '0.82rem',
			lineHeight: '1.8',
			color: theme.palette.mode === 'dark' ? '#FFF' : '#6F8098',
		},
		'.value': {
			fontWeight: 400,
			fontSize: '0.9rem',
			lineHeight: '1.5',
			color: theme.palette.mode === 'dark' ? '#FFF' : theme.palette.primary.main,
		},
	} as SxProps;
	return (
		<Box sx={sxItem}>
			<Box className="label">{props.label}</Box>
			<Box className="value">{props.value}</Box>
		</Box>
	);
};

export default VInvestExpertiseAssets;
